<template>
    <div class="d-flex flex-column">
        <v-btn variant="text" :disabled="loading" class="px-0" @click="onButtonClick">
            <v-icon color="primary" start>{{ loading ? 'mdi-loading mdi-spin' : 'mdi-file-pdf-box' }}</v-icon>
            {{ documentName }}
        </v-btn>
        <span
            v-if="checkboxServicesSelected"
            class="text-caption mt-n3 text--secondary"
            style="font-size: 10px !important"
        >
            {{ `${$t('items_selected')}: ${checkboxServicesSelected.length}` }}
        </span>
        <voucher-dialog
            v-model="isShowDocumentDialog"
            :is-loading="loading"
            :title="documentName"
            @download-voucher="download"
            @send-on-email="sendDocumentOnEmail($event)"
        >
            <template v-slot="{voucherType}">
                <v-textarea
                    v-if="voucherType === 1"
                    v-model="messageText"
                    rows="3"
                    density="compact"
                    variant="outlined"
                    :label="$t('email_form.text')"
                />
                <v-checkbox v-model="isSaveToAttachments" class="ms-n2" :label="$t('save_document_to_attachments')" />
            </template>
        </voucher-dialog>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import {arrayBufferToBase64, downloadData} from '@/utils/helpers'
    import {customDocumentFileName} from '@/utils/document-utils'
    import VoucherDialog from '~/components/account/VoucherDialog.vue'
    import {wrapHtmlMail} from '~/utils/email'
    import {UPDATE_ATTACHMENTS} from '~/utils/event-bus'

    @Component({
        components: {VoucherDialog},
    })
    export class DownloadCustomDocumentButton extends Vue {
        @Prop({required: true}) order
        @Prop({required: true}) template
        @Prop() checkboxServicesSelected

        loading = false

        isShowDocumentDialog = false
        isSaveToAttachments = false

        messageText = null

        async download() {
            if (!this.checkboxServicesSelected?.length) {
                return this.$toast.warning(this.$t('items_selected_error'))
            }
            try {
                this.loading = true
                const orderId = this.order.orderId
                const data = await this.$api.customDocuments.get({
                    orderId,
                    templateId: this.template.id,
                    ...(this.checkboxServicesSelected?.length && {
                        checkboxServicesSelected: this.checkboxServicesSelected.join(','),
                    }),
                    //TODO Need refactoring - check orderType and isAgency on server side, orderType and isAgency duplicate each other
                    orderType: authStore.orderType,
                    isAgency: this.isAgency,
                    localeCode: this.locale,
                })
                downloadData(data, customDocumentFileName(this.order, this.template))
                if (this.isSaveToAttachments) {
                    const base64 = arrayBufferToBase64(data)

                    await this.saveToAttachments(base64)
                }

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.isShowDocumentDialog = false
            } catch (e) {
                this.$toast.error(this.$t('error_message.something_went_wrong'))
            } finally {
                this.loading = false
            }
        }

        onButtonClick() {
            if (this.isTO1) {
                this.isShowDocumentDialog = true
            } else {
                this.download()
            }
        }

        async sendDocumentOnEmail(voucherEmail) {
            try {
                this.loading = true

                const docs = {
                    vouchers: [],
                    invoices: [],
                    documents: [],
                    customDocuments: [
                        {
                            filename: customDocumentFileName(this.order, this.template),
                            id: this.template.id,
                            name: this.template.documentType,
                            selected: true,
                        },
                    ],
                }
                const rs = await this.$api.mail.post({
                    message: {
                        subject: `${this.template.title} - ${this.$t('order')} ${this.order.orderId}`,
                        to: [voucherEmail],
                        cc: [],
                        bcc: [],
                        html: wrapHtmlMail(`${this.messageText || ''}`),
                        attachments: [],
                    },
                    docs,
                    orderId: this.order.orderId,
                    orderType: authStore.orderType,
                    isAgency: authStore.isAgency,
                    checkboxServicesSelected: this.checkboxServicesSelected,
                })

                if (this.isSaveToAttachments) {
                    const documentArrayBuffer = rs?.message?.attachments?.[0]?.content?.data

                    if (documentArrayBuffer) {
                        const base64 = arrayBufferToBase64(documentArrayBuffer)

                        await this.saveToAttachments(base64)
                    }
                }

                this.$toast.success(this.$t('success_message.send_email'))
                this.isShowDocumentDialog = false
            } catch (e) {
                console.error(e)
                this.$toast.error(this.$t('error_message.send_email_error'))
            } finally {
                this.loading = false
            }
        }

        async saveToAttachments(encodedData) {
            this.loading = true
            const data = {
                orderId: this.order.orderId,
                name: customDocumentFileName(this.order, this.template),
                encodedData,
                contentType: 'application/pdf',
                documentType: 'OTHER',
                visibleForAll: false,
            }

            try {
                await this.$api.documents.post(data)
                EventBus.$emit(UPDATE_ATTACHMENTS)
            } catch {
                this.$toast.error(this.$t('error_message.save_error'))
            }
            this.loading = false
        }

        get documentName() {
            if (this.template.title) return this.template.title
            const name = this.template.documentType.split(' ').join('_').toLowerCase()

            return this.$t(name)
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isAgency() {
            return authStore.isAgency
        }

        get locale() {
            return this.$i18n.locale
        }
    }

    export default DownloadCustomDocumentButton
</script>
